import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import Toastify from "../../utils/tostfy";
import { endpoint } from "../../utils/endpoints";
import {
  limitTextInput,
  validateWordCount,
  WORD_COUNT_HINT,
} from "../../utils/textWordValidation";
import { useBackToList } from "../../hooks/useBackToList";
import { formatInr as formatINR } from "../../utils/formatAmount";
import FingerprintVerifyModal from "../../Components/FingerprintCapture/FingerprintVerifyModal";

const ACCOUNTANT_API =
  process.env.REACT_APP_ACCOUNTANT_API;

const parseBody = async (response) => {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return { rawMessage: text };
  }
};

const getErrorMessage = (payload, response) => {
  if (!payload || typeof payload !== "object") {
    return response.statusText || "Something went wrong. Please try again.";
  }
  return (
    payload.errorMessage ||
    payload.message ||
    payload.error ||
    (Array.isArray(payload.errors) ? payload.errors.join(", ") : null) ||
    `Request failed (${response.status})`
  );
};

const C = {
  bg: "#F4F5F8",
  panel: "#FFFFFF",
  soft: "#F9FAFB",
  border: "#E5E7EB",
  text: "#111827",
  muted: "#6B7280",
  primary: "#000053",
  primarySoft: "rgba(0, 0, 83, 0.06)",
  ring: "rgba(0, 0, 83, 0.18)",
};

const toAmount = (v) => Number(v) || 0;
const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

const pickApiAmount = (data, ...keys) => {
  for (const key of keys) {
    const value = data?.[key];
    if (value === null || value === undefined || value === "") continue;
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return 0;
};
const normalizeAmountInput = (raw) => {
  const cleaned = String(raw || "").replace(/[^\d.]/g, "");
  const firstDot = cleaned.indexOf(".");
  if (firstDot === -1) return cleaned;
  const normalized = `${cleaned.slice(0, firstDot + 1)}${cleaned
    .slice(firstDot + 1)
    .replace(/\./g, "")}`;
  const [intPart, decPart = ""] = normalized.split(".");
  return `${intPart}.${decPart.slice(0, 2)}`;
};

const OTP_LEN = 6;
const OTP_TIMEOUT_SEC = 90;
const NORMAL_ADVANCE_MAX = 25000;
const emptyOtpDigits = () => Array(OTP_LEN).fill("");
const onlyDigits = (value) => String(value || "").replace(/\D/g, "");

const normalizeMobilePayload = (profile) => {
  const sourceMobile =
    profile?.mobile_number ||
    profile?.mobile ||
    profile?.phone ||
    profile?.contact_number ||
    "";
  const sourceCountryCode =
    profile?.country_code || profile?.countryCode || profile?.dial_code || "+91";
  const digits = onlyDigits(sourceMobile);
  if (!digits) return null;

  if (digits.length >= 10) {
    return {
      mobile_number: digits.slice(-10),
      country_code: sourceCountryCode.startsWith("+")
        ? sourceCountryCode
        : `+${sourceCountryCode}`,
    };
  }

  return {
    mobile_number: digits,
    country_code: sourceCountryCode.startsWith("+")
      ? sourceCountryCode
      : `+${sourceCountryCode}`,
  };
};

export default function SalaryDisburse() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const backToList = useBackToList("/salary-disbursement");
  const driverId = searchParams.get("driver_id") || "";

  const [detailsLoading, setDetailsLoading] = useState(true);
  const [detailsError, setDetailsError] = useState("");
  const [driverFullName, setDriverFullName] = useState("—");
  const [apiRemainingBalance, setApiRemainingBalance] = useState(0);
  const [apiTotalSalary, setApiTotalSalary] = useState(0);
  const [apiTotalAward, setApiTotalAward] = useState(0);
  const [forceZeroTotalPayable, setForceZeroTotalPayable] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [verifyType, setVerifyType] = useState("");
  const [otp, setOtp] = useState(emptyOtpDigits);
  const [otpFromResponse, setOtpFromResponse] = useState("");
  const [otpSendLoading, setOtpSendLoading] = useState(false);
  const [otpSubmitting, setOtpSubmitting] = useState(false);
  const [otpMeta, setOtpMeta] = useState(null);
  const [otpTimeLeft, setOtpTimeLeft] = useState(OTP_TIMEOUT_SEC);
  const otpRefs = useRef([]);
  const [note, setNote] = useState("");
  /** Enrolled template from viewDriver, used as TargetTemplate on verify. */
  const [driverFingerprint, setDriverFingerprint] = useState("");
  const [fingerprintLoading, setFingerprintLoading] = useState(false);

  const otpExpired = showModal && verifyType === "otp" && otpTimeLeft === 0;

  useEffect(() => {
    if (!showModal || verifyType !== "otp") return;
    if (otpTimeLeft === 0) return;
    const timer = setInterval(() => {
      setOtpTimeLeft((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [showModal, verifyType, otpTimeLeft]);

  const [salaryChecked, setSalaryChecked] = useState(false);
  const [awardChecked, setAwardChecked] = useState(false);
  const [advanceChecked, setAdvanceChecked] = useState(false);

  const [salaryInput, setSalaryInput] = useState("0");
  const [awardInput, setAwardInput] = useState("0");
  const [advanceInput, setAdvanceInput] = useState("0");

  const fingerprintRequired = advanceChecked;

  useEffect(() => {
    if (fingerprintRequired) setVerifyType("fingerprint");
  }, [fingerprintRequired]);

  useEffect(() => {
    if (!driverId) {
      toast.error("Please select a driver from Salary Disbursement.");
      navigate(backToList, { replace: true });
      setDetailsLoading(false);
      return undefined;
    }

    let cancelled = false;
    const load = async () => {
      setDetailsLoading(true);
      setDetailsError("");
      const token = localStorage.getItem("sessionToken");
      if (!token) {
        if (!cancelled) {
          setDetailsError("You are not signed in.");
          setDetailsLoading(false);
        }
        return;
      }

      try {
        const url = `${ACCOUNTANT_API}${endpoint.getDriverSalaryDetails}?driver_id=${encodeURIComponent(
          driverId
        )}`;
        const response = await fetch(url, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const payload = await parseBody(response);
        if (cancelled) return;

        if (!response.ok || !payload?.success) {
          throw new Error(getErrorMessage(payload, response));
        }

        const d = payload.data;
        if (!d || typeof d !== "object") {
          throw new Error("Invalid response from server.");
        }

        setDriverFullName(d.driver_full_name ?? "—");
        setApiRemainingBalance(
          pickApiAmount(d, "remming_balance", "remaining_balance", "remining_balance", "current_balance")
        );
        const rawTotalPayableAmount = d.total_payable_amount;
        const hasTotalPayableAmount =
          rawTotalPayableAmount !== null &&
          rawTotalPayableAmount !== undefined &&
          rawTotalPayableAmount !== "";

        setApiTotalSalary(pickApiAmount(d, "total_payable_amount", "total_salary"));
        setApiTotalAward(pickApiAmount(d, "total_award", "total_awards"));
        setForceZeroTotalPayable(
          hasTotalPayableAmount && Number(rawTotalPayableAmount) === 0
        );
        setDetailsLoading(false);
      } catch (e) {
        if (!cancelled) {
          const msg = e instanceof Error ? e.message : "Failed to load driver salary.";
          setDetailsError(msg);
          setDetailsLoading(false);
          toast.error(msg);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [driverId, navigate]);

  const salarySourceLimit = toAmount(apiTotalSalary);
  const awardSourceAmount = toAmount(apiTotalAward);
  const salaryCap = Math.max(0, salarySourceLimit);
  const awardSourceLimit = Math.max(0, awardSourceAmount);
  const salaryCheckboxDisabled = salarySourceLimit <= 0;
  const awardCheckboxDisabled = awardSourceAmount <= 0;
  const advanceCheckboxDisabled = apiRemainingBalance < 0;

  useEffect(() => {
    if (salaryCheckboxDisabled && salaryChecked) setSalaryChecked(false);
  }, [salaryCheckboxDisabled, salaryChecked]);

  useEffect(() => {
    if (awardCheckboxDisabled && awardChecked) setAwardChecked(false);
  }, [awardCheckboxDisabled, awardChecked]);

  useEffect(() => {
    if (advanceCheckboxDisabled && advanceChecked) {
      setAdvanceChecked(false);
      setAdvanceInput("0");
    }
  }, [advanceCheckboxDisabled, advanceChecked]);

  const normalAdvance = advanceChecked ? toAmount(advanceInput) : 0;
  const salaryAwardLimit = Math.max(0, apiRemainingBalance - normalAdvance);
  const totalPayableAmountTill = forceZeroTotalPayable
    ? 0
    : round2(toAmount(apiTotalSalary) + toAmount(apiTotalAward));

  useEffect(() => {
    // Keep existing values valid when limit changes, but allow free typing.
    const salary = salaryChecked ? toAmount(salaryInput) : 0;
    const award = awardChecked ? toAmount(awardInput) : 0;
    // Per-field source caps.
    let nextSalary = Math.min(salary, salaryCap);
    let nextAward = Math.min(award, awardSourceLimit);

    // Combined balance cap.
    const total = nextSalary + nextAward;
    if (total > salaryAwardLimit) {
      const overflow = total - salaryAwardLimit;
      if (nextAward >= overflow) {
        nextAward = round2(Math.max(0, nextAward - overflow));
      } else {
        nextSalary = round2(Math.max(0, nextSalary - (overflow - nextAward)));
        nextAward = 0;
      }
    }

    if (nextSalary !== salary) setSalaryInput(String(nextSalary));
    if (nextAward !== award) setAwardInput(String(nextAward));
  }, [
    salaryAwardLimit,
    salaryChecked,
    awardChecked,
    salaryInput,
    awardInput,
    salaryCap,
    awardSourceLimit,
  ]);

  const handleSalaryChange = (value) => {
    const n = toAmount(normalizeAmountInput(value));
    const award = awardChecked ? toAmount(awardInput) : 0;
    const maxSalary = Math.max(
      0,
      Math.min(salaryCap, salaryAwardLimit - award)
    );
    setSalaryInput(String(round2(Math.min(n, maxSalary))));
  };

  const handleAwardChange = (value) => {
    const n = toAmount(normalizeAmountInput(value));
    const salary = salaryChecked ? toAmount(salaryInput) : 0;
    const maxAward = Math.max(
      0,
      Math.min(awardSourceLimit, salaryAwardLimit - salary)
    );
    setAwardInput(String(round2(Math.min(n, maxAward))));
  };

  const handleAdvanceChange = (value) => {
    const n = toAmount(normalizeAmountInput(value));
    setAdvanceInput(String(round2(Math.min(n, NORMAL_ADVANCE_MAX))));
  };

  const summary = useMemo(() => {
    const salary = salaryChecked ? Number(salaryInput) || 0 : 0;
    const award = awardChecked ? Number(awardInput) || 0 : 0;
    const normalAdvance = advanceChecked ? Number(advanceInput) || 0 : 0;
    const totalPaid = salary + award + normalAdvance;
    const remaining = round2(apiRemainingBalance - totalPaid);

    return {
      totalAmount: totalPayableAmountTill,
      salaryAmount: salary,
      awardAmount: award,
      normalAdvanceAmount: normalAdvance,
      totalRemainingAmount: remaining,
      totalPaidAmount: totalPaid,
    };
  }, [
    advanceChecked,
    advanceInput,
    apiRemainingBalance,
    totalPayableAmountTill,
    awardChecked,
    awardInput,
    salaryChecked,
    salaryInput,
  ]);

  const salaryMaxAllowed = Math.max(
    0,
    Math.min(
      salaryCap,
      salaryAwardLimit - (awardChecked ? toAmount(awardInput) : 0)
    )
  );
  const awardMaxAllowed = Math.max(
    0,
    Math.min(
      awardSourceLimit,
      salaryAwardLimit - (salaryChecked ? toAmount(salaryInput) : 0)
    )
  );
  const salaryInputDisabled = !salaryChecked || salaryCheckboxDisabled;
  const awardInputDisabled = !awardChecked || awardCheckboxDisabled;
  const salaryRemaining = Math.max(
    0,
    round2(apiTotalSalary - (salaryChecked ? toAmount(salaryInput) : 0))
  );
  const awardRemaining = Math.max(
    0,
    round2(awardSourceLimit - (awardChecked ? toAmount(awardInput) : 0))
  );

  const inputClass =
    "h-8 w-full max-w-[180px] rounded-md border bg-white px-2.5 py-1 text-sm text-gray-900 outline-none transition " +
    "placeholder:text-gray-400 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-400 " +
    "hover:border-gray-300 focus:border-transparent focus:ring-2 focus:ring-[#000053]/15 focus:ring-offset-0";

  /** Align header pills with Remaining panels (same grid track). */
  const amountPairGrid = "grid grid-cols-[minmax(0,1fr)_118px] gap-x-2 sm:grid-cols-[minmax(0,1fr)_124px] sm:gap-x-2.5";
  const statPanelClass =
    "flex min-h-8 w-full flex-col justify-center rounded-md border px-2 py-1 text-right text-xs tabular-nums leading-none";
  const pillClass =
    "inline-flex min-h-8 w-full max-w-full items-center justify-end whitespace-nowrap rounded-md px-2 py-0.5 text-xs font-UbuntuMedium tabular-nums sm:text-sm";

  const closeModal = () => {
    setShowModal(false);
    setOtp(emptyOtpDigits());
    setOtpFromResponse("");
    setOtpMeta(null);
    setOtpSendLoading(false);
    setOtpSubmitting(false);
  };

  const loadAdminProfileForOtp = async (token) => {
    const response = await fetch(`${ACCOUNTANT_API}${endpoint.getAdminProfile}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const payload = await parseBody(response);
    if (!response.ok || !payload?.success) {
      throw new Error(getErrorMessage(payload, response));
    }
    return payload?.data || {};
  };

  /** Reads the driver's enrolled fingerprint template from viewDriver. */
  const loadDriverFingerprint = async () => {
    const token = localStorage.getItem("sessionToken");
    if (!token) {
      toast.error("Session expired. Please log in again.");
      return "";
    }

    setFingerprintLoading(true);
    try {
      const url = `${ACCOUNTANT_API}${endpoint.viewDriver}?driver_id=${encodeURIComponent(
        driverId
      )}`;
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const payload = await parseBody(response);
      if (!response.ok || (payload?.success === false)) {
        throw new Error(getErrorMessage(payload, response));
      }

      const driver = payload?.data?.driver || payload?.data || {};
      const signature = String(
        driver.fingerprint_signature ||
          driver.fingerprintSignature ||
          driver.FingerprintSignature ||
          ""
      ).trim();
      if (!signature) {
        toast.error(
          "This driver has no enrolled fingerprint. Use OTP verification instead."
        );
        return "";
      }
      return signature;
    } catch (err) {
      toast.error(err?.message || "Could not load the driver's fingerprint.");
      return "";
    } finally {
      setFingerprintLoading(false);
    }
  };

  /** Runs after the Mantra verify API confirms the fingerprint matches. */
  const submitFingerprintDisbursement = async () => {
    const token = localStorage.getItem("sessionToken");
    if (!token) {
      toast.error("Session expired. Please log in again.");
      return;
    }

    const response = await fetch(
      `${ACCOUNTANT_API}${endpoint.salaryDisbursement}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          driver_id: driverId,
          normal_advance: advanceChecked ? toAmount(advanceInput) : 0,
          salary_amount: salaryChecked ? toAmount(salaryInput) : 0,
          award_amount: awardChecked ? toAmount(awardInput) : 0,
          note: note.trim(),
          type: "Fingerprint Signature",
        }),
      }
    );

    const payload = await parseBody(response);
    if (
      !response.ok ||
      (payload?.code != null && payload.code !== 200 && payload.code !== 201)
    ) {
      throw new Error(getErrorMessage(payload, response));
    }

    toast.success(payload?.message || "Fingerprint verified and salary disbursed.");
    navigate(backToList, { replace: true });
  };

  const requestSendOtp = async () => {
    const token = localStorage.getItem("sessionToken");
    if (!token) {
      toast.error("Session expired. Please log in again.");
      return false;
    }

    setOtpSendLoading(true);
    try {
      const profile = await loadAdminProfileForOtp(token);
      const normalizedMobile = normalizeMobilePayload(profile);

      const body = {
        type: "Payment Verification",
        // driver_id: driverId,
      };

      if (normalizedMobile?.mobile_number) {
        body.mobile_number = normalizedMobile.mobile_number;
        body.country_code = normalizedMobile.country_code;
      }

      const response = await fetch(`${ACCOUNTANT_API}${endpoint.sendOtp}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });
      const payload = await parseBody(response);
      if (!response.ok || (payload?.code != null && payload.code !== 200 && payload.code !== 201)) {
        throw new Error(getErrorMessage(payload, response));
      }
      const apiOtp = payload?.data?.otp ?? payload?.data?.OTP;
      setOtpFromResponse(apiOtp != null ? String(apiOtp) : "");

      setOtpMeta({
        mobile_number: body.mobile_number || "",
        country_code: body.country_code || "+91",
        type: body.type,
      });
      toast.success(payload?.message || "OTP sent successfully.");
      setOtpTimeLeft(OTP_TIMEOUT_SEC);
      return true;
    } catch (err) {
      toast.error(err?.message || "Could not send OTP.");
      return false;
    } finally {
      setOtpSendLoading(false);
    }
  };

  const handleProceed = () => {
    const noteText = note.trim();
    const noteError = validateWordCount(noteText, "Note");
    if (noteError) {
      toast.error(noteError);
      return;
    }

    const salaryAmount = salaryChecked ? toAmount(salaryInput) : 0;
    const awardAmount = awardChecked ? toAmount(awardInput) : 0;
    const normalAdvanceAmount = advanceChecked ? toAmount(advanceInput) : 0;

    if (salaryAmount <= 0 && awardAmount <= 0 && normalAdvanceAmount <= 0) {
      toast.error("Enter at least one amount in Salary, Award, or Normal Advance.");
      return;
    }

    if (fingerprintRequired) {
      if (verifyType !== "fingerprint") {
        toast.error("Normal advance requires fingerprint verification.");
        return;
      }
    } else if (!verifyType) {
      toast.error("Please select a verification type.");
      return;
    }
    setOtp(emptyOtpDigits());
    setOtpFromResponse("");
    if (verifyType === "otp") {
      requestSendOtp().then((sent) => {
        if (!sent) return;
        setShowModal(true);
      });
      return;
    }

    if (verifyType === "fingerprint") {
      loadDriverFingerprint().then((signature) => {
        if (!signature) return;
        setDriverFingerprint(signature);
        setShowModal(true);
      });
      return;
    }

    setShowModal(true);
  };

  const handleOtpChange = (value, index) => {
    if (!/^\d?$/.test(value)) return;
    const next = [...otp];
    next[index] = value;
    setOtp(next);
    if (value && index < OTP_LEN - 1) {
      otpRefs.current[index + 1]?.focus();
    }
    if (!value && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleResendOtp = async () => {
    const sent = await requestSendOtp();
    if (!sent) return;
    setOtp(emptyOtpDigits());
    otpRefs.current[0]?.focus();
  };

  const handleOtpSubmit = async () => {
    if (otpTimeLeft === 0) {
      toast.error("OTP expired. Please resend OTP and try again.");
      return;
    }
    const entered = otp.join("");
    if (entered.length !== OTP_LEN) {
      toast.error(`Enter the ${OTP_LEN}-digit OTP.`);
      return;
    }
    if (!otpMeta?.type) {
      toast.error("OTP session expired. Please request OTP again.");
      setShowModal(false);
      return;
    }

    const token = localStorage.getItem("sessionToken");
    if (!token) {
      toast.error("Session expired. Please log in again.");
      return;
    }

    setOtpSubmitting(true);
    try {
      const response = await fetch(`${ACCOUNTANT_API}${endpoint.otpVerify}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          mobile_number: otpMeta.mobile_number,
          country_code: otpMeta.country_code,
          otp: entered,
          type: otpMeta.type,
        }),
      });
      const payload = await parseBody(response);
      if (!response.ok || (payload?.code != null && payload.code !== 200 && payload.code !== 201)) {
        throw new Error(getErrorMessage(payload, response));
      }

      if (!otpMeta.mobile_number) {
        throw new Error("Mobile number not found for verification. Please request OTP again.");
      }

      const disbursementResponse = await fetch(
        `${ACCOUNTANT_API}${endpoint.salaryDisbursement}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            driver_id: driverId,
            normal_advance: advanceChecked ? toAmount(advanceInput) : 0,
            salary_amount: salaryChecked ? toAmount(salaryInput) : 0,
            award_amount: awardChecked ? toAmount(awardInput) : 0,
            note: note.trim(),
            type: "OTP Verify",
            verify_mobile_number: {
              country_code: otpMeta.country_code || "+91",
              mobile_number: otpMeta.mobile_number,
            },
          }),
        }
      );
      const disbursementPayload = await parseBody(disbursementResponse);
      if (
        !disbursementResponse.ok ||
        (disbursementPayload?.code != null &&
          disbursementPayload.code !== 200 &&
          disbursementPayload.code !== 201)
      ) {
        throw new Error(getErrorMessage(disbursementPayload, disbursementResponse));
      }

      toast.success(
        disbursementPayload?.message || payload?.message || "OTP verified and salary disbursed."
      );
      closeModal();
      navigate(backToList, { replace: true });
    } catch (err) {
      toast.error(err?.message || "Could not verify OTP.");
    } finally {
      setOtpSubmitting(false);
    }
  };

  if (!driverId) {
    return <Toastify />;
  }

  if (detailsLoading) {
    return (
      <div
        className="flex min-h-[50vh] items-center justify-center font-UbuntuRegular"
        style={{ background: C.bg }}
      >
        <p className="text-sm" style={{ color: C.muted }}>
          Loading driver salary…
        </p>
        <Toastify />
      </div>
    );
  }

  if (detailsError) {
    return (
      <div className="mx-auto max-w-8xl px-4 py-10 font-UbuntuRegular" style={{ background: C.bg }}>
        <p className="mb-4 text-sm" style={{ color: C.text }}>
          {detailsError}
        </p>
        <Link to={backToList} className="btn btn-primary inline-block">
          Back to Salary Disbursement
        </Link>
        <Toastify />
      </div>
    );
  }

  return (
    <div className="max-h-screen font-UbuntuRegular" style={{ background: C.bg }}>
      <div className="mx-auto max-w-8xl px-3 py-4 sm:px-4 lg:px-5">
        <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
          <Link to={backToList} className="btn btn-primary btn-sm shrink-0 py-1.5 px-3 text-sm">
            Back
          </Link>
          </div>
          
        </div>

        <div
          className="mb-4 overflow-hidden rounded-xl border bg-white shadow-sm"
          style={{ borderColor: C.border }}
        >
          <div
            className="flex flex-col gap-2 px-3 py-2.5 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:gap-3 sm:px-4 sm:py-3"
            style={{ background: C.primary }}
          >
            <div>
              <p className="text-sm font-UbuntuMedium uppercase tracking-wide text-white sm:text-base">
                Driver: <span className="font-UbuntuMedium">{driverFullName}</span>
              </p>
              <p className="mt-1 text-xs font-UbuntuMedium uppercase tracking-wide text-white/90 sm:text-sm">
              Total Payable Amount Till:{" "}
                <span className="font-UbuntuMedium text-white">{formatINR(totalPayableAmountTill)}</span>
              </p>
            </div>
            <div className="sm:text-right">
              <p className="text-sm font-UbuntuMedium uppercase tracking-wide text-white sm:text-base">
                Current Remaining:{" "}
                <span className="font-UbuntuMedium">{formatINR(apiRemainingBalance)}</span>
              </p>
            </div>
          </div>

          <div className="border-t px-3 py-3 sm:px-4" style={{ borderColor: C.border }}>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3 xl:gap-3 xl:items-start">
              <div
                className="flex flex-col rounded-lg border p-3"
                style={{ borderColor: C.border, background: C.primarySoft }}
              >
                <div className={`${amountPairGrid} items-center`}>
                  <label
                    className={`inline-flex min-w-0 items-center gap-2 ${
                      salaryCheckboxDisabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"
                    }`}
                  >
                    <input
                      type="checkbox"
                      className="h-3.5 w-3.5 shrink-0 rounded border-gray-300 disabled:cursor-not-allowed"
                      style={{ accentColor: C.primary }}
                      checked={salaryChecked}
                      disabled={salaryCheckboxDisabled}
                      onChange={(e) => setSalaryChecked(e.target.checked)}
                    />
                    <span className="text-sm font-UbuntuMedium" style={{ color: C.text }}>
                      Salary
                    </span>
                  </label>
                  <div className="flex justify-end">
                    <span className={pillClass} style={{ background: C.panel, color: C.primary }}>
                      {formatINR(apiTotalSalary)}
                    </span>
                  </div>
                </div>

                <div className="mt-2.5 flex flex-1 flex-col">
                  <label
                    className="mb-1 block text-[11px] font-UbuntuMedium uppercase tracking-wide"
                    style={{ color: C.muted }}
                  >
                    Enter salary
                  </label>
                  <div className={`${amountPairGrid} items-center`}>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={salaryInput}
                      onChange={(e) => handleSalaryChange(e.target.value)}
                      disabled={salaryInputDisabled}
                      className={inputClass}
                      style={{ borderColor: C.border }}
                    />
                    <div className={statPanelClass} style={{ borderColor: C.border, background: C.panel }}>
                      <span className="text-[9px] font-UbuntuMedium uppercase leading-tight tracking-wide" style={{ color: C.muted }}>
                        Remaining
                      </span>
                      <span className="mt-0.5 text-xs font-UbuntuMedium leading-none text-gray-700">{formatINR(salaryRemaining)}</span>
                    </div>
                  </div>
                  <p className="mt-1 text-[11px] leading-snug" style={{ color: C.muted }}>
                    Max allowed: {formatINR(salaryMaxAllowed)}
                  </p>
                </div>
              </div>

              <div
                className="flex flex-col rounded-lg border p-3"
                style={{ borderColor: C.border, background: C.primarySoft }}
              >
                <div className={`${amountPairGrid} items-center`}>
                  <label
                    className={`inline-flex min-w-0 items-center gap-2 ${
                      awardCheckboxDisabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"
                    }`}
                  >
                    <input
                      type="checkbox"
                      className="h-3.5 w-3.5 shrink-0 rounded border-gray-300 disabled:cursor-not-allowed"
                      style={{ accentColor: C.primary }}
                      checked={awardChecked}
                      disabled={awardCheckboxDisabled}
                      onChange={(e) => setAwardChecked(e.target.checked)}
                    />
                    <span className="text-sm font-UbuntuMedium" style={{ color: C.text }}>
                      Award
                    </span>
                  </label>
                  <div className="flex justify-end">
                    <span className={pillClass} style={{ background: C.panel, color: C.primary }}>
                      {formatINR(apiTotalAward)}
                    </span>
                  </div>
                </div>

                <div className="mt-2.5 flex flex-1 flex-col">
                  <label
                    className="mb-1 block text-[11px] font-UbuntuMedium uppercase tracking-wide"
                    style={{ color: C.muted }}
                  >
                    Enter award
                  </label>
                  <div className={`${amountPairGrid} items-center`}>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={awardInput}
                      onChange={(e) => handleAwardChange(e.target.value)}
                      disabled={awardInputDisabled}
                      className={inputClass}
                      style={{ borderColor: C.border }}
                    />
                    <div className={statPanelClass} style={{ borderColor: C.border, background: C.panel }}>
                      <span className="text-[9px] font-UbuntuMedium uppercase leading-tight tracking-wide" style={{ color: C.muted }}>
                        Remaining
                      </span>
                      <span className="mt-0.5 text-xs font-UbuntuMedium leading-none text-gray-700">{formatINR(awardRemaining)}</span>
                    </div>
                  </div>
                  <p className="mt-1 text-[11px] leading-snug" style={{ color: C.muted }}>
                    Max allowed: {formatINR(awardMaxAllowed)}
                  </p>
                </div>
              </div>

              <div
                className="flex flex-col rounded-lg border p-3"
                style={{ borderColor: C.border, background: C.primarySoft }}
              >
                <div className={`${amountPairGrid} items-center`}>
                  <label
                    className={`inline-flex min-w-0 items-center gap-2 ${
                      advanceCheckboxDisabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"
                    }`}
                  >
                    <input
                      type="checkbox"
                      className="h-3.5 w-3.5 shrink-0 rounded border-gray-300"
                      style={{ accentColor: C.primary }}
                      checked={advanceChecked}
                      disabled={advanceCheckboxDisabled}
                      onChange={(e) => setAdvanceChecked(e.target.checked)}
                    />
                    <span className="text-sm font-UbuntuMedium" style={{ color: C.text }}>
                      Normal advance
                    </span>
                  </label>
                  <div className="flex justify-end">
                    <span className={pillClass} style={{ background: C.panel, color: C.primary }}>
                      {advanceChecked ? formatINR(normalAdvance) : "—"}
                    </span>
                  </div>
                </div>

                <div className="mt-2.5 flex flex-1 flex-col">
                  <label
                    className="mb-1 block text-[11px] font-UbuntuMedium uppercase tracking-wide"
                    style={{ color: C.muted }}
                  >
                    Enter advance
                  </label>
                  <div className={`${amountPairGrid} items-center`}>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={advanceInput}
                      onChange={(e) => handleAdvanceChange(e.target.value)}
                      disabled={!advanceChecked || advanceCheckboxDisabled}
                      className={inputClass}
                      style={{ borderColor: C.border }}
                    />
                    <div className={statPanelClass} style={{ borderColor: C.border, background: C.panel }}>
                      <span className="text-[9px] font-UbuntuMedium uppercase leading-tight tracking-wide" style={{ color: C.muted }}>
                        Remaining
                      </span>
                      <span className="mt-0.5 text-xs font-UbuntuMedium leading-none text-gray-400">—</span>
                    </div>
                  </div>
                  <p className="mt-1 text-[11px] leading-snug" style={{ color: C.muted }}>
                    {advanceCheckboxDisabled
                      ? "Normal advance is disabled when current remaining is negative."
                      : `Max allowed: ${formatINR(NORMAL_ADVANCE_MAX)}`}
                  </p>
                </div>
              </div>
            </div>

            {/* <div
              className="mt-3 flex gap-2 rounded-md px-2.5 py-1.5 text-xs"
              style={{ background: C.primarySoft, color: C.muted }}
            >
              <span className="shrink-0 font-UbuntuMedium leading-snug" style={{ color: C.primary }}>
                Rule
              </span>
              <span className="leading-snug">Salary + Award cannot exceed (Current Balance − Normal Advance).</span>
            </div> */}
          </div>
        </div>

        <div
          className="mb-4 rounded-xl border bg-white p-3 shadow-sm"
          style={{ borderColor: C.border }}
        >
          <label className="mb-1 block text-xs font-UbuntuMedium sm:text-sm" style={{ color: C.text }}>
            Note<span className="text-danger"> *</span>
          </label>
          <textarea
            rows={2}
            value={note}
            onChange={(e) => setNote(limitTextInput(e.target.value))}
            placeholder="Add any additional notes about this payment"
            className="w-full resize-none rounded-lg border px-2.5 py-1.5 text-sm outline-none transition placeholder:text-gray-400 focus:border-transparent focus:ring-2 focus:ring-[#000053]/15 focus:ring-offset-0"
            style={{ borderColor: C.border }}
          />
          <p className="mt-1 text-xs text-gray-500">{WORD_COUNT_HINT}</p>
        </div>

        <div className="mb-4">
          <h2 className="mb-2 text-sm font-UbuntuMedium sm:text-base" style={{ color: C.text }}>
            Payment summary
          </h2>
          <div
            className="overflow-hidden rounded-xl border shadow-sm"
            style={{ borderColor: C.border, background: C.panel }}
          >
            <div className="divide-y divide-gray-200 text-xs sm:text-sm">
              <div className="flex items-center justify-between gap-3 px-3 py-2 sm:px-4">
                <span style={{ color: C.muted }}>Total Payable Amount</span>
                <span className="font-UbuntuMedium tabular-nums" style={{ color: C.text }}>
                  {formatINR(summary.totalAmount)}
                </span>
              </div>
              <div className="flex items-center justify-between gap-3 px-3 py-2 sm:px-4">
                <span style={{ color: C.muted }}>Salary</span>
                <span className="font-UbuntuMedium tabular-nums" style={{ color: C.text }}>
                  {formatINR(summary.salaryAmount)}
                </span>
              </div>
              <div className="flex items-center justify-between gap-3 px-3 py-2 sm:px-4">
                <span style={{ color: C.muted }}>Award</span>
                <span className="font-UbuntuMedium tabular-nums" style={{ color: C.text }}>
                  {formatINR(summary.awardAmount)}
                </span>
              </div>
              <div className="flex items-center justify-between gap-3 px-3 py-2 sm:px-4">
                <span style={{ color: C.muted }}>Normal Advance</span>
                <span className="font-UbuntuMedium tabular-nums" style={{ color: C.text }}>
                  -{formatINR(summary.normalAdvanceAmount)}
                </span>
              </div>
              <div className="flex items-center justify-between gap-3 px-3 py-2 sm:px-4">
                <span style={{ color: C.muted }}>Total Remaining </span>
                <span className="font-UbuntuMedium tabular-nums" style={{ color: C.text }}>
                  {formatINR(summary.totalRemainingAmount)}
                </span>
              </div>
            </div>
            <div
              className="flex items-center justify-between gap-3 px-3 py-2.5 sm:px-4"
              style={{ background: C.soft }}
            >
              <span className="text-sm font-UbuntuBold sm:text-base" style={{ color: C.text }}>
                Total paid amount
              </span>
              <span className="text-base font-UbuntuBold tabular-nums sm:text-lg" style={{ color: C.primary }}>
                {formatINR(summary.totalPaidAmount)}
              </span>
            </div>
          </div>
        </div>

        <div
          className="flex flex-col gap-2 rounded-xl border bg-white p-3 shadow-sm sm:flex-row sm:flex-wrap sm:items-center sm:justify-between"
          style={{ borderColor: C.border }}
        >
          <div className="flex w-full min-w-0 flex-col gap-1 sm:max-w-xs sm:flex-1">
            <select
              className="h-9 w-full min-w-0 rounded-md border bg-white px-2.5 text-sm outline-none transition focus:border-transparent focus:ring-2 focus:ring-[#000053]/15"
              style={{ borderColor: C.border }}
              value={verifyType}
              onChange={(e) => {
                if (fingerprintRequired) {
                  setVerifyType("fingerprint");
                  return;
                }
                setVerifyType(e.target.value);
              }}
            >
              <option value="">Select verification type</option>
              {!fingerprintRequired ? (
                <option value="otp">OTP verification</option>
              ) : null}
              <option value="fingerprint">Fingerprint verification</option>
            </select>
            {fingerprintRequired ? (
              <p className="m-0 text-xs" style={{ color: C.muted }}>
                Normal advance allows fingerprint verification only.
              </p>
            ) : (
              <p className="m-0 text-xs" style={{ color: C.muted }}>
                You can verify with OTP or fingerprint.
              </p>
            )}
          </div>

          <button
            type="button" 
            onClick={handleProceed}
            disabled={otpSendLoading || fingerprintLoading}
            className="h-9 shrink-0 rounded-md px-5 text-sm font-UbuntuMedium text-white shadow-md transition hover:opacity-95 active:scale-[0.99] sm:ml-auto"
            style={{ background: C.primary, boxShadow: `0 6px 18px ${C.ring}` }}
          >
            {otpSendLoading
              ? "Sending OTP..."
              : fingerprintLoading
              ? "Loading fingerprint..."
              : "Proceed now"}
          </button>
        </div>
      </div> 

      <FingerprintVerifyModal
        open={showModal && verifyType === "fingerprint"}
        onClose={closeModal}
        driverId={driverId}
        driverName={driverFullName}
        targetTemplate={driverFingerprint}
        onVerified={submitFingerprintDisbursement}
      />

      {showModal && verifyType === "otp" ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-[2px]">
          <div
            className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl"
            style={{ boxShadow: `0 25px 50px -12px ${C.ring}` }}
          >
            <div className="px-4 py-2.5 text-sm font-UbuntuMedium text-white" style={{ background: C.primary }}>
              OTP verification
            </div>
                <div className="px-4 py-5">
                  <p className="text-center text-sm text-gray-600">
                    Enter the {OTP_LEN}-digit OTP to verify and submit this disbursement for{" "}
                    <span className="font-UbuntuMedium text-gray-900">{driverFullName}</span>.
                  </p>
                  {otpFromResponse ? (
                    <div className="mt-3 rounded-md border border-[#000053]/20 bg-[#000053]/5 px-3 py-2 text-center">
                      <p className="text-[11px] font-UbuntuMedium uppercase tracking-wide text-gray-600">
                        OTP from response
                      </p>
                      <p className="mt-0.5 font-mono text-base tracking-[0.2em] text-[#000053]">
                        {otpFromResponse}
                      </p>
                    </div>
                  ) : null}
                  <div className="mt-4 flex justify-center gap-1.5 sm:gap-2">
                    {otp.map((digit, i) => (
                      <input
                        key={i}
                        ref={(el) => {
                          otpRefs.current[i] = el;
                        }}
                        type="text"
                        inputMode="numeric"
                        maxLength={1}
                        autoComplete="one-time-code"
                        disabled={otpSubmitting || otpSendLoading || otpExpired}
                        className="h-10 w-10 rounded-md border text-center text-sm font-UbuntuMedium outline-none transition focus:border-transparent focus:ring-2 focus:ring-[#000053]/15 sm:h-11 sm:w-11"
                        style={{ borderColor: C.border }}
                        value={digit}
                        onChange={(e) => handleOtpChange(e.target.value, i)}
                        onKeyDown={(e) => {
                          if (e.key === "Backspace" && !otp[i] && i > 0) {
                            otpRefs.current[i - 1]?.focus();
                          }
                        }}
                      />
                    ))}
                  </div>
                  <p className={`mt-3 text-center text-sm ${otpExpired ? "text-danger" : "text-gray-700"}`}>
                    Remaining Time:{" "}
                    <b>
                      {String(Math.floor(otpTimeLeft / 60)).padStart(2, "0")}:
                      {String(otpTimeLeft % 60).padStart(2, "0")}s
                    </b>
                  </p>
                  {otpExpired ? (
                    <p className="mt-1 text-center text-xs text-danger">
                      OTP expired. Cancel or resend OTP to continue.
                    </p>
                  ) : null}
                  <p className="mt-3 text-center text-xs text-gray-600">
                    Didn&apos;t get the code?{" "}
                    <button
                      type="button"
                      onClick={handleResendOtp}
                      disabled={otpSendLoading || otpSubmitting || otpTimeLeft > 0}
                      className="font-UbuntuMedium underline transition hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-50"
                      style={{ color: C.primary }}
                    >
                      {otpSendLoading ? "Sending…" : "Resend OTP"}
                    </button>
                  </p>
                </div>
                <div className="grid grid-cols-2 border-t" style={{ borderColor: C.border }}>
                  <button
                    type="button"
                    onClick={closeModal}
                    disabled={otpSubmitting}
                    className="border-r border-gray-200 py-2.5 text-sm font-UbuntuMedium text-gray-700 transition hover:bg-gray-50 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleOtpSubmit}
                    disabled={otpSubmitting || otpSendLoading || otpExpired}
                    className="py-2.5 text-sm font-UbuntuMedium text-white transition hover:opacity-95 disabled:opacity-60"
                    style={{ background: C.primary }}
                  >
                    {otpSubmitting ? "Verifying…" : "Submit"}
                  </button>
                </div>
          </div>
        </div>
      ) : null}
      <Toastify />
    </div>
  );
}
